import axios from 'axios';
import { getIsraelConfig } from '../config/israelConfig';

// No-auth axios instance for the Israel patient app, pointed at the Israel
// backend (the same backend the Israel mobile app uses; see
// config/israelConfig.ts). All endpoints are keyed by an opaque per-patient
// `token` taken from the /schedule/:token URL.
const israelApi = axios.create({
  baseURL: getIsraelConfig().israelBackendBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// The mobile app opens the schedule page with ?mobile_app=true. Forward the
// marker on every API call so the backend attributes the request to the app
// (schedule_log device column). Decided per request from the CURRENT page
// URL - no persistence, so a plain link in the same tab is never misread.
israelApi.interceptors.request.use((config) => {
  try {
    if (
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('mobile_app') === 'true'
    ) {
      config.params = { ...(config.params || {}), mobile_app: 'true' };
    }
  } catch {
    /* non-browser environment */
  }
  return config;
});

// ---------------------------------------------------------------------------
// Context response
// ---------------------------------------------------------------------------
// GET /public/schedule/{token}
// Returns the patient's name and their list of meetings. This is the mobile
// backend's real shape — a flat `meetings[]` array whose `status` drives the
// whole UI. See the ticket / backend probes:
//   status "active"          -> upcoming meeting; edit+cancel iff can_update
//   status "schedule"        -> placeholder to be booked (id === null)
//   status "future_schedule" -> not bookable yet; shows the API text (grey)
//   status "done"            -> completed/history
export type MeetingStatus =
  | 'active'
  | 'schedule'
  | 'future_schedule'
  | 'done'
  | (string & {});

export interface IsraelMeeting {
  id: number | null; // null for a not-yet-booked "schedule" placeholder
  meetingtype_id: number;
  therapist_type: string; // e.g. "פסיכאטר", "מנהל תיק" — used in card header
  meeting_type: string; // e.g. "מעקב" — used in card header
  length: number; // minutes
  status: MeetingStatus;
  can_update: boolean; // reschedule/cancel allowed
  start: number | null; // epoch ms in the patient's clinic timezone; null if unbooked
  from_date: string | null; // "YYYY-MM-DD" — start of the bookable window
  to_date: string | null; // "YYYY-MM-DD" — end of the bookable window
  user_id: number; // therapist id
  meeting_title_number: number | null; // ordinal within the series
  text: string | null; // pre-localized label (e.g. "לחץ לקביעת פגישה", "בוצעה")
  hint?: string | null; // role hint under the text (e.g. "בסמיכות לפגישת פסיכאטר")
}

export interface IsraelScheduleContext {
  patient_name: string;
  meetings: IsraelMeeting[];
}

// ---------------------------------------------------------------------------
// Slots
// ---------------------------------------------------------------------------
// A single bookable time slot.
//   value   — opaque token echoed back on book/reschedule (a naive datetime;
//             DO NOT reformat it client-side — timezone is server-applied).
//   display — pre-formatted HH:MM in the clinic's timezone; show this.
//   date    — "YYYY-MM-DD" the slot falls on.
export interface Slot {
  value: string;
  display: string;
  date: string;
  // Therapist (Yoman resource) that owns this slot — present for pool slots;
  // echoed back on book so the event gets a concrete resource.
  resource_id?: string;
}

// Response of the first-available-month endpoint.
export interface FirstAvailableMonth {
  month: string | null; // "YYYY-MM"
  from_date: string | null; // "YYYY-MM-DD"
  to_date: string | null; // "YYYY-MM-DD"
  slots: Slot[];
  // Yoman did not answer in time. NOT the same as "no slots": the availability
  // is unknown, so the screen must say so rather than show an empty calendar.
  unavailable?: boolean;
}

// A slot list plus whether it is trustworthy. An empty `slots` with
// `unavailable` set means "we could not find out", not "nothing is free".
export interface SlotsResult {
  slots: Slot[];
  unavailable: boolean;
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

// Dedupe concurrent identical GETs: React 18 StrictMode double-runs mount
// effects in dev, and slot fetches are expensive (a live Yoman call each).
// While a GET is in flight, an identical request shares the same promise.
const inflightGets = new Map<string, Promise<unknown>>();

function dedupedGet<T>(
  url: string,
  params?: Record<string, unknown>
): Promise<T> {
  const key = `${url}?${JSON.stringify(params ?? {})}`;
  const existing = inflightGets.get(key);
  if (existing) return existing as Promise<T>;
  const request = israelApi
    .get(url, params ? { params } : undefined)
    .then((res) => res.data as T)
    .finally(() => {
      inflightGets.delete(key);
    });
  inflightGets.set(key, request);
  return request;
}

// Load the patient's schedule context (name + meetings).
export async function getScheduleContext(
  token: string
): Promise<IsraelScheduleContext> {
  return dedupedGet<IsraelScheduleContext>(`/public/schedule/${token}`);
}

// --- Reschedule an EXISTING meeting (keyed by meeting_id) ------------------

// First month (within the meeting's reschedule window) that has any
// available slots.
export async function getFirstAvailableMonth(
  token: string,
  meetingId: number,
  fromDate?: string | null,
  toDate?: string | null
): Promise<FirstAvailableMonth> {
  return dedupedGet<FirstAvailableMonth>(
    `/public/schedule/${token}/first-available-month`,
    {
      meeting_id: meetingId,
      ...(fromDate ? { from_date: fromDate } : {}),
      ...(toDate ? { to_date: toDate } : {}),
    }
  );
}

// First month with availability for a brand-new meeting of this type,
// clamped to the item's booking window when provided.
export async function getNewMeetingFirstAvailableMonth(
  token: string,
  meetingTypeId: number,
  fromDate?: string | null,
  toDate?: string | null
): Promise<FirstAvailableMonth> {
  return dedupedGet<FirstAvailableMonth>(
    `/public/schedule/${token}/first-available-month`,
    {
      meeting_type_id: meetingTypeId,
      ...(fromDate ? { from_date: fromDate } : {}),
      ...(toDate ? { to_date: toDate } : {}),
    }
  );
}

// Available reschedule slots for a meeting within a date range (inclusive).
export async function getRescheduleSlots(
  token: string,
  meetingId: number,
  fromDate: string,
  toDate: string
): Promise<SlotsResult> {
  const data = await dedupedGet<{ slots?: Slot[]; unavailable?: boolean }>(
    `/public/schedule/${token}/slots`,
    { meeting_id: meetingId, from_date: fromDate, to_date: toDate }
  );
  return { slots: data?.slots ?? [], unavailable: !!data?.unavailable };
}

// The answer to a booking request.
//   start  — when the meeting was ACTUALLY booked, ISO with offset. The server
//            verifies the Yoman event before answering, so this is the only
//            trustworthy time to show the patient: Yoman can place an event at
//            a different time than the one asked for.
//   reason — why a booking was refused; "slot_taken" means Yoman would not
//            give the slot the patient picked.
export interface BookResult {
  ok: boolean;
  yoman_id?: string;
  start?: string;
  reason?: string;
}

// Move an existing meeting to a new slot (start = a slot's `value`).
// waitingList: the patient opted into the waiting list ("contact me if an
// earlier slot frees up") — stored on the meeting's waiting_list field.
export async function rescheduleMeeting(
  token: string,
  meetingId: number,
  start: string,
  waitingList = false
): Promise<BookResult> {
  const { data } = await israelApi.post(
    `/public/schedule/${token}/reschedule`,
    { meeting_id: meetingId, start, waiting_list: waitingList }
  );
  return data;
}

// --- Book a NEW meeting for a "schedule" placeholder -----------------------
// Availability is filtered by meeting_type_id only (user_id was verified NOT
// to affect results). user_id is forwarded on booking so the meeting binds to
// the intended therapist — per the ticket; confirm on the backend if bookings
// land on the wrong therapist.

// Available slots for a brand-new meeting within a date range (inclusive).
export async function getNewMeetingSlots(
  token: string,
  meetingTypeId: number,
  fromDate: string,
  toDate: string,
  userId?: number
): Promise<SlotsResult> {
  const data = await dedupedGet<{ slots?: Slot[]; unavailable?: boolean }>(
    `/public/schedule/${token}/new-slots`,
    {
      meeting_type_id: meetingTypeId,
      from_date: fromDate,
      to_date: toDate,
      ...(userId != null ? { user_id: userId } : {}),
    }
  );
  return { slots: data?.slots ?? [], unavailable: !!data?.unavailable };
}

// Book a brand-new meeting at a chosen slot (start = a slot's `value`).
// resourceId (from the chosen slot) is required for pool slots so the Yoman
// event gets a concrete resource.
// waitingList: the patient opted into the waiting list ("contact me if an
// earlier slot frees up") — stored on the meeting's waiting_list field.
export async function bookMeeting(
  token: string,
  meetingTypeId: number,
  start: string,
  userId?: number,
  resourceId?: string,
  waitingList = false
): Promise<BookResult> {
  const { data } = await israelApi.post(`/public/schedule/${token}/book`, {
    meeting_type_id: meetingTypeId,
    start,
    ...(userId != null ? { user_id: userId } : {}),
    ...(resourceId ? { resource_id: resourceId } : {}),
    waiting_list: waitingList,
  });
  return data;
}

// --- Cancel ----------------------------------------------------------------

// Cancel an existing meeting, optionally with a reason.
export async function cancelMeeting(
  token: string,
  meetingId: number,
  reason?: string
): Promise<{ ok: boolean }> {
  const { data } = await israelApi.post(`/public/schedule/${token}/cancel`, {
    meeting_id: meetingId,
    ...(reason ? { reason } : {}),
  });
  return data;
}

export default israelApi;
